import { z } from 'zod';

export const ThemeAttrSchema = z.enum(['light', 'mint', 'dark', 'indigo-light', 'indigo-dark']);
export const ThemeNameSchema = z.enum(['warmsunset', 'coolmist', 'deepstudio', 'indigoday', 'indigonight']);
export const DensitySchema = z.enum(['cosy', 'compact']);
export const DecorationSchema = z.enum(['clean', 'playful', 'max']);
export const LocaleSchema = z.enum(['ru', 'tr', 'en']);

/**
 * Text-size control A / A+ / A++ · MOBILE_READABILITY slot (2026-06-06).
 * base = current sizes (A) · lg = x1.15 (A+) · xl = x1.3 (A++).
 * Optional for backward compat: old localStorage payloads stay valid;
 * the deployed /ui-prefs edge function (non-strict zod) strips the key
 * until redeployed with this schema — see CF-MR-EDGE-REDEPLOY.
 */
export const FontScaleSchema = z.enum(['base', 'lg', 'xl']);

export const UiPrefsSchema = z.object({
  theme: ThemeNameSchema,
  density: DensitySchema,
  decoration: DecorationSchema,
  reduceMotion: z.boolean().optional(),
  fontScale: FontScaleSchema.optional(),
  /**
   * Аватар-бадди профиля (Profile v2 · 9 JUL 2026): ключ варианта из
   * BUDDY_VARIANTS (buddies.tsx). Optional для обратной совместимости —
   * пустое значение = дневная ротация (rotatingBuddyVariant). Старый
   * deployed /ui-prefs edge strip'ает ключ до редеплоя (CF-MR-EDGE-REDEPLOY).
   */
  avatarBuddy: z.string().optional(),
});

/**
 * Pomodoro user-tunable settings · per-device by default per OAQ-2a-8 = B
 * (sync only progress fields · pomo settings live in localStorage). Schema kept
 * here so future toggle (sync · per-device) doesn't churn type imports.
 */
export const PomoSettingsSchema = z.object({
  workMin: z.number().int(),
  shortBreakMin: z.number().int(),
  longBreakMin: z.number().int(),
  cyclesBeforeLongBreak: z.number().int(),
  soundPreset: z.string().optional(),
});

/**
 * Pomodoro daily counter log · per-device only (per Stage A §2.1 A `pomo.log`).
 */
export const PomoLogSchema = z.object({
  date: z.string(),
  todayCount: z.number().int(),
  cycleCount: z.number().int(),
  pendingReflection: z.boolean().optional(),
});

export type ThemeAttr = z.infer<typeof ThemeAttrSchema>;
export type ThemeName = z.infer<typeof ThemeNameSchema>;
export type Density = z.infer<typeof DensitySchema>;
export type Decoration = z.infer<typeof DecorationSchema>;
export type Locale = z.infer<typeof LocaleSchema>;
export type FontScale = z.infer<typeof FontScaleSchema>;
export type UiPrefs = z.infer<typeof UiPrefsSchema>;
export type PomoSettings = z.infer<typeof PomoSettingsSchema>;
export type PomoLog = z.infer<typeof PomoLogSchema>;
